import React, { useMemo, useState } from 'react';
import { Btn, KPI, KPIRow, Loading, Msg, Section, fmtDate, useAdminGet } from '../../kit';
import { Badge, Check, Drawer } from '../emailKit';
import { LifecyclePill, PERSONA_LABEL, TagPills } from '../../tags/shared';
import { Ago, PEOPLE, TimelineTarget, emailPeople, fmtDay, num, safeUrl } from './shared';

/**
 * One person's timeline — any address, account or not (people/timeline):
 * the account header, what we sent and what SendGrid saw happen to each
 * message, site visits, and for an account everything User 360 knows, newest
 * first and grouped by day.
 */

type Group = 'email' | 'visit' | 'activity' | 'account';
const GROUPS: Array<{ key: Group; label: string }> = [
  { key: 'email', label: 'E-mails' }, { key: 'visit', label: 'Site visits' },
  { key: 'activity', label: 'Product activity' }, { key: 'account', label: 'Account, tags & imports' },
];

/** Icon, colour and filter group per event kind (admin_people + admin_user360 kinds). */
const KINDS: Record<string, { glyph: string; color: string; group: Group }> = {
  email_sent: { glyph: '✉', color: '#2563EB', group: 'email' },
  email_failed: { glyph: '!', color: '#B91C1C', group: 'email' },
  email_delivered: { glyph: '✓', color: '#0891B2', group: 'email' },
  email_open: { glyph: '◉', color: '#15803D', group: 'email' },
  email_click: { glyph: '↗', color: '#7C3AED', group: 'email' },
  email_bounce: { glyph: '✕', color: '#DC2626', group: 'email' },
  email_deferred: { glyph: '⏲', color: '#CA8A04', group: 'email' },
  email_spam: { glyph: '⚑', color: '#B91C1C', group: 'email' },
  email_unsubscribe: { glyph: '⊘', color: '#9A3412', group: 'email' },
  visit: { glyph: '◎', color: '#0E7490', group: 'visit' },
  login: { glyph: '→', color: '#475569', group: 'activity' },
  search: { glyph: '⌕', color: '#1D4ED8', group: 'activity' },
  chat: { glyph: '✦', color: '#4F46E5', group: 'activity' },
  message: { glyph: '✎', color: '#6366F1', group: 'activity' },
  save: { glyph: '★', color: '#7C3AED', group: 'activity' },
  contact: { glyph: '✆', color: '#0F766E', group: 'activity' },
  feedback: { glyph: '☆', color: '#CA8A04', group: 'activity' },
  billing: { glyph: '¤', color: '#15803D', group: 'account' },
  lifecycle: { glyph: '●', color: '#64748B', group: 'account' },
  account: { glyph: '●', color: '#64748B', group: 'account' },
  tag: { glyph: '#', color: '#9333EA', group: 'account' },
  import: { glyph: '⇪', color: '#B45309', group: 'account' },
};
const styleOf = (kind: string) => KINDS[kind] || { glyph: '•', color: '#64748B', group: 'account' as Group };

const dayKey = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Unknown date'
    : d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};
const timeOf = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const MessageFlags: React.FC<{ m: any }> = ({ m }) => (
  <span className="inline-flex gap-1 flex-wrap">
    {m.failed && <Badge tone="red">not sent</Badge>}
    {m.delivered && <Badge tone="blue">delivered</Badge>}
    {m.opened && <Badge tone="green">opened</Badge>}
    {m.clicked && <Badge tone="green">clicked</Badge>}
    {m.bounced && <Badge tone="red">bounced</Badge>}
    {m.spam && <Badge tone="red">spam report</Badge>}
    {m.unsubscribed && <Badge tone="amber">unsubscribed</Badge>}
    {!m.failed && !m.delivered && !m.opened && !m.bounced && <Badge tone="grey">no events yet</Badge>}
  </span>
);

const Timeline: React.FC<TimelineTarget & { onClose: () => void }> = ({ email, userId, onClose }) => {
  const params = userId ? { user_id: userId } : { email: email || '' };
  const res = useAdminGet<any>(PEOPLE, 'timeline', params, [email, userId]);
  const d = res.data;
  const person = d?.person;
  const s = d?.summary || {};
  const [groups, setGroups] = useState<Group[]>(GROUPS.map(g => g.key));
  const [allMail, setAllMail] = useState(false);

  const days = useMemo(() => {
    const out: Array<[string, any[]]> = [];
    (d?.events || []).filter((e: any) => groups.includes(styleOf(e.kind).group)).forEach((e: any) => {
      const k = dayKey(e.at);
      const last = out[out.length - 1];
      if (last && last[0] === k) last[1].push(e); else out.push([k, [e]]);
    });
    return out;
  }, [d, groups]);

  const messages: any[] = d?.messages || [];
  const shownMail = allMail ? messages : messages.slice(0, 8);
  const title = d?.email || email || person?.email || 'Timeline';
  const subtitle = person
    ? [person.name, person.company, person.country].filter(Boolean).join(' · ') || 'Account'
    : d ? 'No account for this address' : '';

  return (
    <Drawer title={title} subtitle={subtitle} onClose={onClose}>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !d ? <Loading label="Reading everything we know about this address…" /> : d && (
        <>
          <div className="bg-white rounded-xl border border-grayscale-300 p-4 flex flex-col gap-3">
            {person ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <LifecyclePill value={person.lifecycle} subtag={person.subtag} label={person.lifecycle_label} />
                {person.persona && (
                  <span className="inline-flex items-center rounded-full border border-grayscale-400 px-2 py-0.5 text-[11px] text-grayscale-1200"
                    title="Primary persona">{person.persona_label || PERSONA_LABEL[person.persona] || person.persona}</span>
                )}
                <TagPills tags={person.tags} />
                <TagPills tags={person.auto_tags} />
                {person.invite_sent && <span className="text-[11px] text-grayscale-1100">invite sent</span>}
              </div>
            ) : (
              <div className="text-sm text-grayscale-1200">
                Not an account. Shown: what we sent to this address, what happened to it, and any import it was in.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div><div className="text-grayscale-1100">First added</div><div className="text-grayscale-1300">{fmtDay(s.first_added)}</div></div>
              <div><div className="text-grayscale-1100">Last activity</div><div className="text-grayscale-1300"><Ago value={s.last_activity} /></div></div>
              {person && <>
                <div><div className="text-grayscale-1100">Last signed in</div><div className="text-grayscale-1300"><Ago value={person.last_login_at} empty="never" /></div></div>
                <div><div className="text-grayscale-1100">Profile</div><div className="text-grayscale-1300">{num(person.profile_pct)}% complete</div></div>
              </>}
            </div>
            {(d.imports || []).length > 0 && (
              <ul className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
                {d.imports.map((i: any) => (
                  <li key={`${i.batch_id}-${i.row_index}`}>
                    In import <b>“{i.source_label}”</b> ({i.filename}, row {i.row_index}) on {fmtDay(i.created_at)}
                    {i.created_by ? ` by ${i.created_by}` : ''} — {String(i.status).replace(/_/g, ' ')}
                    {i.detail ? ` · ${i.detail}` : ''}
                  </li>
                ))}
              </ul>
            )}
            <KPIRow>
              <KPI label="E-mails sent" value={num(s.sent)} />
              <KPI label="Delivered" value={num(s.delivered)} />
              <KPI label="Opened" value={num(s.opened)} />
              <KPI label="Clicked" value={num(s.clicked)} />
              <KPI label="Bounced" value={num(s.bounced)} />
              <KPI label="Site visits" value={num(s.visits)} />
            </KPIRow>
            {person?.user_id && (
              <div className="flex gap-2">
                <Btn small onClick={() => emailPeople([person.user_id])}>E-mail this person</Btn>
                <Btn small onClick={res.reload}>Refresh</Btn>
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <Section title={`E-mails (${messages.length})`}
              right={messages.length > 8
                ? <Btn small onClick={() => setAllMail(v => !v)}>{allMail ? 'Show the latest 8' : `Show all ${messages.length}`}</Btn>
                : undefined}>
              <div className="flex flex-col divide-y divide-grayscale-200">
                {shownMail.map(m => (
                  <div key={m.key} className="py-1.5 flex flex-col gap-0.5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-sm text-grayscale-1300 font-medium">{m.subject}</span>
                      <MessageFlags m={m} />
                    </div>
                    <div className="text-[11px] text-grayscale-1100">
                      {fmtDate(m.at)}{m.source ? ` · ${m.source}` : ''}{m.error ? ` · ${m.error}` : ''}
                    </div>
                    {(m.clicks || []).map((c: any, i: number) => (
                      <div key={i} className="text-[11px] text-grayscale-1200 truncate">
                        clicked {fmtDate(c.at)}: {safeUrl(c.url)
                          ? <a href={safeUrl(c.url)} target="_blank" rel="noopener noreferrer" className="underline">{c.url}</a>
                          : (c.url || 'a link')}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title={`Timeline (${num((d.events || []).length)}${d.truncated ? ', newest only' : ''})`}
            right={<div className="flex gap-3 flex-wrap">
              {GROUPS.map(g => (
                <Check key={g.key} checked={groups.includes(g.key)}
                  onChange={on => setGroups(gs => on ? [...gs, g.key] : gs.filter(x => x !== g.key))}>
                  {g.label}
                </Check>
              ))}
            </div>}>
            {!days.length && <div className="text-sm text-grayscale-1100 py-3">Nothing recorded for this address yet.</div>}
            {days.map(([day, evs]) => (
              <div key={day} className="flex flex-col gap-1">
                <div className="text-xs font-bold text-grayscale-1200 font-grotesk sticky top-0 bg-white py-0.5">{day}</div>
                <ol className="ml-3 border-l border-grayscale-300">
                  {evs.map((e, i) => {
                    const st = styleOf(e.kind);
                    const url = safeUrl(e.meta?.url);
                    return (
                      <li key={i} className="relative pl-6 py-1">
                        <span aria-hidden
                          className="absolute -left-3 top-1 w-6 h-6 rounded-full flex items-center justify-center text-[11px] text-white"
                          style={{ backgroundColor: st.color }}>{st.glyph}</span>
                        <div className="text-sm text-grayscale-1300">
                          <span className="text-[11px] text-grayscale-1100 mr-2">{timeOf(e.at)}</span>
                          {e.title}
                        </div>
                        {(e.detail || url || e.meta?.reason) && (
                          <div className="text-xs text-grayscale-1100 break-words">
                            {e.detail}
                            {e.meta?.reason && <span className="text-red-700"> · {e.meta.reason}</span>}
                            {url && <> · <a href={url} target="_blank" rel="noopener noreferrer" className="underline">{e.meta.url}</a></>}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </Section>

          {(d.warnings || []).length > 0 && (
            <details className="text-xs text-grayscale-1100">
              <summary className="cursor-pointer">{d.warnings.length} source(s) could not be read</summary>
              <ul className="list-disc pl-5 pt-1">{d.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}</ul>
            </details>
          )}
        </>
      )}
    </Drawer>
  );
};

export default Timeline;
